import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Upload,
  Download,
  FileText,
  FileCode,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { dataSource } from "@/lib/dataSource";
import { useToast } from "@/hooks/use-toast";
import { useBreadcrumbs } from "./BreadcrumbContext";

interface ImportPreviewRow {
  status: "Valid" | "Error" | "Warning";
  type: string;
  code: string;
  name: string;
  issues: string;
}

type FileFormat = "csv" | "json" | "docx" | "pdf";

interface ImportResult {
  success: boolean;
  processedRows: number;
  errors: Array<{
    row: number;
    message: string;
    data: any;
  }>;
  warnings: Array<{
    row: number;
    message: string;
    data: any;
  }>;
  data?: Array<{
    status: "Valid" | "Error" | "Warning";
    type: string;
    code: string;
    name: string;
    issues: string;
  }>;
}

export function ImportExport() {
  const [importFormat, setImportFormat] = useState<FileFormat>("json");
  const [dryRunMode, setDryRunMode] = useState(true);
  const [validateReferences, setValidateReferences] = useState(true);
  const [checkDuplicates, setCheckDuplicates] = useState(true);
  const [importPreview, setImportPreview] = useState<ImportPreviewRow[]>([]);
  const [importStats, setImportStats] = useState({
    total: 0,
    errors: 0,
    created: 0,
    skipped: 0,
    updated: 0,
    warnings: 0,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Export settings
  const [exportScope, setExportScope] = useState<"all" | "datatypes" | "attributes">(
    "all"
  );
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeAudit, setIncludeAudit] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const { toast } = useToast();

  const { setBreadcrumbs } = useBreadcrumbs();

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: `Import/Export`,
        path: `/import`,
      },
    ]);
  }, []);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setImportStats({
      total: 0,
      errors: 0,
      created: 0,
      skipped: 0,
      updated: 0,
      warnings: 0,
    });
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    // Show preview immediately
    const mockData: ImportPreviewRow[] = [
      {
        status: "Valid",
        type: "CUSTOMER",
        code: "NEW_CUST",
        name: "New Customer Type",
        issues: "—",
      },
      {
        status: "Error",
        type: "PRODUCT",
        code: "invalid-code-$",
        name: "Invalid Product",
        issues: "Invalid code format",
      },
      {
        status: "Warning",
        type: "SUBSCRIPTION",
        code: "TRIAL",
        name: "Trial Subscription",
        issues: "Duplicate name found",
      },
    ];

    setImportPreview(mockData);
    // setImportStats({ total: mockData.length, errors: 1, warnings: 1 });
    setShowPreview(true);
  };

  const handleStartImport = async () => {
    setImportStats({
      total: 0,
      errors: 0,
      created: 0,
      skipped: 0,
      updated: 0,
      warnings: 0,
    });
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to import",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const result = await dataSource.importData(
        selectedFile,
        importFormat,
        dryRunMode,
        validateReferences,
        checkDuplicates,
        "insert",             // mode
        exportScope
      );
      // Transform API response to preview format
      const previewData: ImportPreviewRow[] = Array.isArray(result.data?.rows)
        ? result.data.rows
        : [];
      // const process = result.data.types.created + result.data.types.skipped + result.data.types.updated + result.data.types.errors

      setImportPreview(previewData);
      setImportStats({
        total: result.processedRows,
        errors: result.data.Datatypes.errors,
        created: result.data.Datatypes.created,
        skipped: result.data.Datatypes.skipped,
        updated: result.data.Datatypes.updated,
        warnings: result.warnings.length,
      });
      setShowPreview(true);
      console.log("dryRunMode", dryRunMode)
      console.log("result", result)
      console.log("showPreview", showPreview)
      if (result.success) {
        toast({
          title: dryRunMode ? "Dry Run Completed" : "Import Successful",
          description: dryRunMode
            ? `Processed ${result.processedRows} rows with ${result.errors.length} errors and ${result.warnings.length} warnings`
            : `Successfully imported ${result.processedRows - result.data.Datatypes.errors - result.data.Datatypes.skipped
            } rows`,
        });
      } else {
        toast({
          title: "Import Completed with Errors",
          description: `Processed ${result.processedRows} rows with ${result.errors.length} errors`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error importing data:", error);
      toast({
        title: "Import Failed",
        description:
          error instanceof Error
            ? error.message
            : "Error importing data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleProcessImport = async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    try {
      // Run actual import (not dry run)
      const result = await dataSource.importData(
        selectedFile,
        importFormat,
        false, // dryRun = false for actual import
        validateReferences,
        checkDuplicates,
      );

      if (result.success) {
        toast({
          title: "Import Successful",
          description: `Successfully imported ${result.processedRows - result.errors.length
            } rows`,
        });
        // Clear preview after successful import
        setShowPreview(false);
        setSelectedFile(null);
      } else {
        toast({
          title: "Import Completed with Errors",
          description: `Processed ${result.processedRows} rows with ${result.errors.length} errors`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error processing import:", error);
      toast({
        title: "Import Failed",
        description:
          error instanceof Error
            ? error.message
            : "Error processing import. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  //   const handleExport = async (format: "CSV" | "JSON") => {
  //     setIsExporting(true);
  //     try {
  //       const blob = await dataSource.exportData(
  //         exportScope,
  //         format.toLowerCase() as "csv" | "json",
  //         // includeDeleted
  //       );

  //       const timestamp = new Date().toISOString().split("T")[0];
  //       const filename = `workfinity-${exportScope}-export-${timestamp}.${format.toLowerCase()}`;
  // console.log("blob",blob)
  //       const url = URL.createObjectURL(blob);
  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.download = filename;
  //       link.style.display = "none";
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       URL.revokeObjectURL(url);

  //       toast({
  //         title: "Export Successful",
  //         description: `Successfully exported data as ${format}`,
  //       });
  //     } catch (error) {
  //       console.error("Error exporting data:", error);
  //       toast({
  //         title: "Export Failed",
  //         description:
  //           error instanceof Error
  //             ? error.message
  //             : "Error exporting data. Please try again.",
  //         variant: "destructive",
  //       });
  //     } finally {
  //       setIsExporting(false);
  //     }
  //   };
  const handleExport = async (format: "CSV" | "JSON" | "DOCX" | "PDF") => {
    if (format === "CSV") {
      setIsExportingCsv(true);
    } else if (format === "JSON") {
      setIsExportingJson(true);
    } else if (format === "DOCX") {
      setIsExportingWord(true);
    } else {
      setIsExportingPdf(true);
    }

    try {
      const blob = await dataSource.exportData(
        exportScope,
        format.toLowerCase() as "csv" | "json"
      );

      // Determine filename
      const timestamp = new Date().toISOString().split("T")[0];
      let filename = `workfinity-${exportScope}-export-${timestamp}`;

      console.log("blob", blob.type)
      if (blob.type === "application/zip") {
        filename += ".zip";
      } else if (blob.type === "text/csv") {
        filename += ".csv";
      } else if (blob.type === "application/json") {
        filename += ".json";
      } else if (blob.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
        filename += ".docx";
      } else if (blob.type === "application/pdf") {
        filename += ".pdf";
      } else {
        filename += ".bin";
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: `Successfully exported data as ${filename.split(".").pop()}`,
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      toast({
        title: "Export Failed",
        description:
          error instanceof Error
            ? error.message
            : "Error exporting data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExportingCsv(false);
      setIsExportingJson(false);
      setIsExportingWord(false);
      setIsExportingPdf(false);
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Valid":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "Error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "Warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Valid":
        return "bg-green-100 text-green-800";
      case "Error":
        return "bg-red-100 text-red-800";
      case "Warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  console.log("importStats", importStats)
  return (
    <div className="min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-xl font-semibold text-black">
          Import / Export
        </h2>
        <p className="text-black mt-1 text-md md:text-base">
          Bulk data operations with validation and preview
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        {/* Import Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Import Data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="format">File Format</Label>
              <Select
                value={importFormat}
                onValueChange={(value: FileFormat) => setImportFormat(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="docx">Word</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="file">Upload File</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-workfinity-400 transition-colors">
                <Upload className="h-8 w-8 text-black mx-auto mb-2" />
                <p className="text-sm text-black mb-2">
                  Drag and drop files here or{" "}
                  <label className="text-workfinity-600 cursor-pointer underline">
                    browse
                    <input
                      type="file"
                      className="hidden"
                      accept={importFormat === "csv" ? ".csv" : importFormat === "docx" ? ".docx" : importFormat === "pdf" ? ".pdf" : ".json"}
                      onChange={handleFileUpload}
                    />
                  </label>
                </p>
                <p className="text-xs text-black">
                  {selectedFile
                    ? selectedFile.name
                    : `Supports ${importFormat.toUpperCase()} files up to 10MB`}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {/* <div className="flex items-center space-x-2">
                <Checkbox
                  id="validateReferences"
                  checked={validateReferences}
                  onCheckedChange={(checked) =>
                    setValidateReferences(checked === true)
                  }
                />
                <Label htmlFor="validateReferences" className="text-sm">
                  Validate references
                </Label>
              </div> */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="checkDuplicates"
                  checked={checkDuplicates}
                  onCheckedChange={(checked) =>
                    setCheckDuplicates(checked === true)
                  }
                />
                <Label htmlFor="checkDuplicates" className="text-sm">
                  Check for duplicates
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="dryRun"
                  checked={dryRunMode}
                  onCheckedChange={(checked) => setDryRunMode(checked === true)}
                />
                <Label htmlFor="dryRun" className="text-sm">
                  Dry run (preview only)
                </Label>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                onClick={handleStartImport}
                disabled={isImporting || !selectedFile}
                className="bg-workfinity-500 hover:bg-workfinity-600"
              >
                {isImporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {isImporting ? "Processing..." : "Start Import"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Export Data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Export Scope</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="all"
                    name="exportScope"
                    value="all"
                    checked={exportScope === "all"}
                    onChange={(e) =>
                      setExportScope(
                        e.target.value as "all" | "datatypes" | "attributes"
                      )
                    }
                    className="text-workfinity-600 focus:ring-workfinity-500"
                  />
                  <Label htmlFor="all">Full Dataset</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="datatypes"
                    name="exportScope"
                    value="datatypes"
                    checked={exportScope === "datatypes"}
                    onChange={(e) =>
                      setExportScope(
                        e.target.value as "all" | "datatypes" | "attributes"
                      )
                    }
                    className="text-workfinity-600 focus:ring-workfinity-500"
                  />
                  <Label htmlFor="types">DataTypes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="attributes"
                    name="exportScope"
                    value="attributes"
                    checked={exportScope === "attributes"}
                    onChange={(e) =>
                      setExportScope(
                        e.target.value as "all" | "datatypes" | "attributes"
                      )
                    }
                    className="text-workfinity-600 focus:ring-workfinity-500"
                  />
                  <Label htmlFor="codes">Attributes</Label>
                </div>
              </div>
            </div>

            {/* <div>
              <Label>Format Options</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="metadata"
                    checked={includeMetadata}
                    onCheckedChange={(checked) =>
                      setIncludeMetadata(checked === true)
                    }
                  />
                  <Label htmlFor="metadata">Include metadata</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="audit"
                    checked={includeAudit}
                    onCheckedChange={(checked) =>
                      setIncludeAudit(checked === true)
                    }
                  />
                  <Label htmlFor="audit">Include audit fields</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="deleted"
                    checked={includeDeleted}
                    onCheckedChange={(checked) =>
                      setIncludeDeleted(checked === true)
                    }
                  />
                  <Label htmlFor="deleted">Include deleted records</Label>
                </div>
              </div>
            </div> */}

            <div className="space-y-2">
              <Button
                onClick={() => handleExport("CSV")}
                disabled={isExportingCsv}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                {isExportingCsv ? "Exporting..." : "Export as CSV"}
              </Button>
              <Button
                onClick={() => handleExport("JSON")}
                disabled={isExportingJson}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <FileCode className="h-4 w-4 mr-2" />
                {isExportingJson ? "Exporting..." : "Export as JSON"}
              </Button>
              <Button
                onClick={() => handleExport("DOCX")}
                disabled={isExportingWord}
                className="w-full  bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <FileCode className="h-4 w-4 mr-2" />
                {isExportingWord ? "Exporting..." : "Export as WORD"}
              </Button>
              <Button
                onClick={() => handleExport("PDF")}
                disabled={isExportingPdf}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <FileCode className="h-4 w-4 mr-2" />
                {isExportingPdf ? "Exporting..." : "Export as PDF"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Import Preview Table */}
      {showPreview && (
        <Card className="">
          {importStats.total !== 0 &&
            <CardHeader>
              <div className="flex items-center justify-between">
                {/* <CardTitle>Import Preview</CardTitle> */}
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-black">
                    {importStats.total} rows,{" "}
                    <span className="text-green-600">
                      {importStats.created} created
                    </span>
                    ,{" "}
                    <span className="text-red-600">
                      {importStats.errors} errors
                    </span>
                    ,{" "}
                    <span className="text-yellow-600">
                      {importStats.warnings} warnings
                    </span>
                    ,{" "}
                    <span className="text-blue-600">
                      {importStats.skipped} skipped
                    </span>
                  </span>
                  {dryRunMode && (
                    <Button
                      onClick={handleProcessImport}
                      disabled={isImporting}
                      className="bg-workfinity-500 hover:bg-workfinity-600"
                    >
                      {isImporting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      Process Import
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          }
          {/* <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Issues</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importPreview.map((row, index) => (
                    <TableRow
                      key={index}
                      className={
                        row.status === "Error"
                          ? "bg-red-50"
                          : row.status === "Warning"
                            ? "bg-yellow-50"
                            : ""
                      }
                    >
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(row.status)}
                          <Badge className={getStatusColor(row.status)}>
                            {row.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{row.type}</TableCell>
                      <TableCell className="font-mono">{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell
                        className={
                          row.status === "Error"
                            ? "text-red-600"
                            : row.status === "Warning"
                              ? "text-yellow-600"
                              : "text-gray-500"
                        }
                      >
                        {row.issues}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent> */}
        </Card>
      )}
    </div>
  );
}
